import { Component } from "react";
import { Card, Row, Col, Badge, Button, OverlayTrigger, Popover, Modal, Dropdown, Spinner } from "react-bootstrap";
import { GoSync, GoPencil, GoTrash, GoPlus } from "react-icons/go";
import { LuClipboardCopy, LuClipboardPaste, LuDownload } from "react-icons/lu";
import { BsThreeDotsVertical } from "react-icons/bs";
import Tooltip from "../../controls/common/tooltip";
import Clipboard from "../../controls/common/clipboard";
import Converter from "../converter";
import EditMessageContentModal from "../../editor/details/content";
import CreateSessionObjectModal from "./create";
import Utils from "context/utils";
import SessionContext from "../session/context";
import "./index.css";

export default class DetailsPane extends Component {
  static contextType = SessionContext;
  constructor(props) {
    super(props);
    this.state = {
      editModalVisible: false,
      editObjectAlias: null,
      editObjectValue: null,
      createModalVisible: false,
      deleteModalVisible: false,
      deleteObjectAlias: null
    };
    this.mounted = true;
  }

  componentDidMount() {
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentWillUnmount() {
    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
    this.mounted = false;
  }

  exportToClipboard() {
    const converter = new Converter(this.props.shell.shellInterface);
    const definition = converter.reactFlowToBlobhubDefinition(this.props.shell.nodes, this.props.shell.edges);
    const text = JSON.stringify(definition, null, 2)
    Clipboard.to(text);
  }

  importFromClipboard() {
    Clipboard.from().then((text) => {
      let definition = null;
      try {
        definition = JSON.parse(text);
        if ( definition["components"].length < 1 ) {
          return;
        }
      } catch (error) {
        console.error(error.message);
        return;
      }

      const converter = new Converter(this.props.shell.shellInterface);
      const [nodes, edges] = converter.blobhubDefinitionToReactFlow(definition, this.props.shell.manifest);

      // Offset new workflow to the right of an existing one
      // 1. Calculate the right boundary of an existing workflow
      let rightMost = 0;
      for ( const node of this.props.shell.nodes ) {
        let currentRight = node.position.x;
        for ( const port of node.data.component["ports"] ) {
          if ( "width" == port["route"] ) {
            currentRight += port["value"]["integer"];
          }
        }
        if ( currentRight > rightMost ) {
          rightMost = currentRight;
        }
      }
      // 2. Calculate the left boundary of a newly pasted workflow
      let leftMost = 0;
      for ( const node of nodes ) {
        if ( node.position.x < leftMost ) {
          leftMost = node.position.x;
        }
      }
      // 3. Compute offset
      const offset = Math.min(leftMost - rightMost, 0) * -1 + 25;
      // 4. Apply offset
      for ( const node of nodes ) {
        node.position.x += offset;
      }

      // Post process notes
      for ( const node of nodes ) {
        // Regenerate workflow IDs
        const newId = Utils.generateUniqueId();
        node.id = newId;
        node.data.component.id = newId;
      }

      // Register enw nodes and edges
      const mergedNodes = this.props.shell.nodes.concat(nodes);
      const mergedEdges = this.props.shell.edges.concat(edges);
      this.props.shell.workflowInterface.setNodes(mergedNodes, true);
      this.props.shell.workflowInterface.setEdges(mergedEdges, true);
    });
  }

  openEditModal(alias, value) {
    this.setState({
      editModalVisible: true,
      editObjectAlias: alias,
      editObjectValue: Utils.deepCopy(value)
    });
  }

  closeEditModal() {
    this.setState({
      editModalVisible: false,
      editObjectAlias: null,
      editObjectValue: null
    });
  }

  openDeleteModal(alias) {
    this.setState({
      deleteModalVisible: true,
      deleteObjectAlias: alias
    });
  }

  closeDeleteModal() {
    this.setState({
      deleteModalVisible: false,
      deleteObjectAlias: null
    });
  }

  handleDeleteConfirm() {
    if (this.state.deleteObjectAlias) {
      this.context.deleteSessionObject(this.state.deleteObjectAlias);
    }
    this.closeDeleteModal();
  }

  downloadSessionObject(alias, data) {
    const filename = `${this.context.session.id}_${alias}.json`;
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }

  handleSaveSessionObject(newValue) {
    this.context.uploadSessionObject(
      this.state.editObjectAlias,
      newValue
    );
  }

  openCreateModal() {
    this.setState({
      createModalVisible: true
    });
  }

  closeCreateModal() {
    this.setState({
      createModalVisible: false
    });
  }

  handleCreateSessionObject(alias) {
    // 1. Close create modal
    this.closeCreateModal();

    // 2. Prepare default data
    const defaultData = {
      type: "message",
      message: {
        role: "user",
        attributes: {
          id: Utils.generateUniqueId(),
          created_at: Utils.currentTimeUtcIso()
        },
        content: []
      }
    };

    // 3. Open edit modal
    this.openEditModal(alias, defaultData);
  }

  render() {
    return (
      <>
        {this.context.session &&
          <>
            <Card.Text as="h6">Session Objects</Card.Text>
            <hr />
            <Card.Text>
              {null != this.context.sessionObjects ?
                <>
                  {0 != this.context.sessionObjects.length ?
                    <>
                      {this.context.sessionObjects.map((object, index) => {
                        const data = this.context.sessionData[object.alias];

                        return (
                          <Row style={{"height": "33px"}}>
                            <Col className="d-flex align-items-center">
                              {data ?
                                <OverlayTrigger trigger={['hover', 'focus']} placement="bottom" overlay={
                                  <Popover id={`popover-positioned-bottom`} style={{ maxWidth: "100%", width: "auto" }}>
                                    <Popover.Content style={{ fontSize: "12px" }}>
                                      <div><strong>Size:</strong> {Utils.formatBytes(JSON.stringify(data).length)}</div>
                                      {object.updatedAt &&
                                        <div><strong>Updated At:</strong> {Utils.formatCompactDateTime(object.updatedAt)}</div>
                                      }
                                    </Popover.Content>
                                  </Popover>
                                }>
                                  <Badge variant="secondary" className="title-tag-badge">{object.alias}</Badge>
                                </OverlayTrigger>
                              : <Badge variant="secondary" className="title-tag-badge">{object.alias}</Badge>
                              }
                              &nbsp;&nbsp;
                              <span className="text-muted small">{(data && data.type) || "unknown"}</span>
                            </Col>
                              <Col sm={"auto"}  className="d-flex align-items-center">
                                {this.context.loadingSessionObjects.has(object.alias) &&
                                  <Spinner animation="border" role="status" size="sm" style={{marginRight: "10px"}}>
                                    <span className="sr-only">Loading...</span>
                                  </Spinner>
                                }
                                {data &&
                                  <Dropdown className="d-inline-block playground-details-action-dropdown">
                                    <Dropdown.Toggle
                                      variant="light"
                                      size="sm"
                                      className="align-middle playground-action-dropdown-toggle"
                                      disabled={!this.props.shell.shellInterface.canEditRevision()}
                                      style={{ marginTop: "-2px" }}
                                    >
                                      <BsThreeDotsVertical />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      { "message" === data.type && this.props.shell.shellInterface.canEditRevision() &&
                                        <Dropdown.Item onClick={() => this.openEditModal(object.alias, data)}>
                                          <GoPencil/>&nbsp;&nbsp;Edit Object
                                        </Dropdown.Item>
                                      }
                                      { this.props.shell.shellInterface.canEditRevision() &&
                                        <Dropdown.Item onClick={() => this.openDeleteModal(object.alias)}>
                                          <GoTrash/>&nbsp;&nbsp;Delete Object
                                        </Dropdown.Item>
                                      }                                      
                                      <Dropdown.Item onClick={() => this.context.loadSessionObject(object.alias)}>
                                        <GoSync/>&nbsp;&nbsp;Refresh Object
                                      </Dropdown.Item>                                      
                                      <Dropdown.Divider />
                                      <Dropdown.Item onClick={() => Clipboard.to(JSON.stringify(data, null, 2))}>
                                        <LuClipboardCopy/>&nbsp;&nbsp;Copy Object
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => this.downloadSessionObject(object.alias, data)}>
                                        <LuDownload/>&nbsp;&nbsp;Download Object
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                }
                              </Col>
                          </Row>
                        );
                      })}
                    </>
                  : <>No objects are available within the selected session.</> }
                </>
              : <>Loading session objects...</> }
            </Card.Text>

            {this.props.shell.shellInterface.canEditRevision() &&
              <>
                <Card.Text as="h6">Actions</Card.Text>
                <hr />
                <Card.Text style={{"margin-top": "20px"}}>
                  <Button variant="light" size="sm" onClick={() => {
                    this.context.loadSessionContent();
                  }}><GoSync />&nbsp;&nbsp;Refresh Session Objects</Button>
                </Card.Text>
                <Card.Text>
                  Pulls the list of objects persisted within the session and loads its contents.
                </Card.Text>

                <Card.Text style={{"margin-top": "20px"}}>
                  <Button variant="light" size="sm" onClick={() => this.openCreateModal()}>
                    <GoPlus /> New Object
                  </Button>
                </Card.Text>
                <Card.Text>
                  Creates a new session object and opens it for editing.
                </Card.Text>
              </>
            }
          </>
        }

        {!this.context.session &&
          <>
            <Card.Text as="h6">Selection</Card.Text>
            <hr />
            <Card.Text>
              Select session or widget to see its properties displayed here.
            </Card.Text>
          </>
        }

        <Card.Text as="h6">Export</Card.Text>
        <hr />
        <Card.Text>
          <Row>
            <Col className="align-self-center">
              Export playground definition to clipboard.
            </Col>
            <Col sm={"auto"}>
              <span className="float-right">
                <Tooltip text="Export to clipboard">
                  <Button size="sm" variant="light" onClick={(event) => { this.exportToClipboard(); }}>
                    <LuClipboardCopy />
                  </Button>
                </Tooltip>
              </span>
            </Col>
          </Row>
        </Card.Text>

        {this.props.shell.shellInterface.canEditRevision() &&
          <>
            <Card.Text as="h6">Import</Card.Text>
            <hr />
            <Card.Text>
              <Row>
                <Col className="align-self-center">
                  Import playground definition from clipboard.
                </Col>
                <Col sm={"auto"}>
                  <span className="float-right">
                    <Tooltip text="Import from clipboard">
                      <Button size="sm" variant="light" onClick={(event) => { this.importFromClipboard(); }}>
                        <LuClipboardPaste />
                      </Button>
                    </Tooltip>
                  </span>
                </Col>
              </Row>
            </Card.Text>
          </>
        }
        {this.renderEditModal()}
        {this.renderCreateModal()}
        {this.renderDeleteModal()}
      </>
    );
  }

  renderEditModal() {
    if ( !this.state.editModalVisible ) {
      return null;
    }

    return (
      <EditMessageContentModal
        show={this.state.editModalVisible}
        portValue={this.state.editObjectValue}
        onSave={(newValue) => this.handleSaveSessionObject(newValue)}
        onHide={() => this.closeEditModal()}
        shell={this.props.shell}
      />
    );
  }

  renderCreateModal() {
    if ( !this.state.createModalVisible ) {
      return null;
    }

    const existingAliases = this.context.sessionObjects 
      ? this.context.sessionObjects.map(obj => obj.alias) 
      : [];

    return (
      <CreateSessionObjectModal
        show={this.state.createModalVisible}
        existingAliases={existingAliases}
        onSave={(alias) => this.handleCreateSessionObject(alias)}
        onHide={() => this.closeCreateModal()}
      />
    );
  }

  renderDeleteModal() {
    if (!this.state.deleteModalVisible) {
      return null;
    }

    return (
      <Modal
        show={this.state.deleteModalVisible}
        onHide={() => this.closeDeleteModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Session Object</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete session object <strong>{this.state.deleteObjectAlias}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => this.closeDeleteModal()}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => this.handleDeleteConfirm()}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
